import React, { useCallback, useEffect, useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  KeyValue,
  TitleHeader,
  Typography,
  useDataTable,
} from 'web-library';
import SummaryBaseInfo from './SummaryBaseInfo';
import { candidateOrSubjectListType } from '../../../utils/ComponentUtils';
import CandidateSubjectListTabs from '../../../components/tabs/CandidateSubjectListTabs';
import { ComponentUsage } from '../../../components/tabs/CandidateSubjectListTabs/model';
import { usePostGroupExamRegistrationSummary } from '../../../api-refactored/group-exam-registration/usePostGroupExamRegistrationSummary';
import ExamFeeDetails from '../../../components/ExamFeeDetails';
import SummaryTransactionDetail from './SummaryTransactionDetail';
import { RegistrationRequestTab } from '../../../api-refactored/group-exam-registration/usePostReviewGroupExamRegistration';
import RouteKey from '../../../routes/key';

const useStyles = makeStyles()((theme) =>
({
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  gstRegNoBox: { marginTop: theme.spacing(4) },
  subsection: {
    marginBottom: theme.spacing(3),
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  backButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(25),
  },
}));

const { exam } = RouteKey.child;
const { coordinatorExamRegistrations } = RouteKey.exam.child;

const CoordinatorPaymentSummary = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid: applicationUuid } = useParams<any>();
  const [selectedTab, setSelectedTab] = useState(0);
  const getCandidateOrSubjectType = candidateOrSubjectListType(selectedTab);

  const { state: candidateTabTableState, dispatch: candidateTabTableDispatch } = useDataTable();
  const summaryMutation = usePostGroupExamRegistrationSummary({
    defaultOnSuccessInput: {
      getCandidateOrSubjectType,
      tableDispatch: candidateTabTableDispatch,
    },
  });

  const { data: summaryData, mutate: postSummaryMutate } = summaryMutation;
  const transactionDetails = {
    transactionNo:
      summaryData?.payment.transactionNo === '-' ? 'N/A' : summaryData?.payment.transactionNo,
    paymentStatus: summaryData?.payment.paymentStatus,
    internalRef:
      summaryData?.payment.internalTransactionRef === '-'
        ? 'N/A'
        : summaryData?.payment.internalTransactionRef,
    paymentMode:
      summaryData?.payment.paymentMode === '-' ? 'N/A' : summaryData?.payment.paymentMode,
  };

  const isIPsle =
    summaryData?.groupedLevelFees?.length &&
    summaryData?.groupedLevelFees.filter((item) => item.examLevel === 'iPSLE').length;

  const retrieveCandidateSummaryTabs = useCallback(() => {
    postSummaryMutate({
      applicationUuid,
      apiPathParam: {
        order: candidateTabTableState.order,
        page: candidateTabTableState.page,
        pageSize: candidateTabTableState.size,
      },
      tab: RegistrationRequestTab.CANDIDATE_LIST,
      paymentStatus: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    retrieveCandidateSummaryTabs();
  }, [retrieveCandidateSummaryTabs]);

  return (
    <Box>
      <TitleHeader title={t('exam->>RegistrationSummary.ViewRegistration')} />
      {summaryData && (
        <ContentTemplate type="full">
          <SummaryBaseInfo
            applicationId={summaryData.applicationId}
            coordinatorName={summaryData.coordinatorName}
            examYear={summaryData.examYear}
            examLevel={summaryData.examLevel}
            applicationStatus={summaryData.applicationStatus}
            applicationDateTime={summaryData.applicationDateTime}
            examRegistrationType={summaryData.examRegistrationType}
          />
          <CandidateSubjectListTabs
            tabStateHandler={{ selectedTab, setSelectedTab }}
            applicationUuid={applicationUuid}
            getCandidateOrSubjectType={getCandidateOrSubjectType}
            candidateDataTableHandler={{ candidateTabTableState, candidateTabTableDispatch }}
            useMutationHook={summaryMutation}
            componentUsageFor={ComponentUsage.SUMMARY}
            totalFee={summaryData.payment.totalFee}
          />
          {(!isIPsle || summaryData.payment.gstIndicator) && summaryData.gstRegNo && (
            <Box className={classes.gstRegNoBox}>
              <KeyValue
                id="gstRegistrationNumber"
                keyText={t('components->>SelectedSubjectReview.GST Reg No.')}
                valueText={summaryData.gstRegNo}
              />
            </Box>
          )}
          <Divider className={classes.divider} />
          <Box id="grouped-exam-fees">
            <Typography variant="h3" className={classes.subsection}>
              {t('exam->>ReviewExam.ExamFees')}
            </Typography>
            <ExamFeeDetails
              groupedLevelFees={summaryData.groupedLevelFees}
              payment={summaryData.payment}
            />
          </Box>
          <Divider className={classes.divider} />
          <SummaryTransactionDetail {...transactionDetails} />
          <Button
            id="button-selectSubject-back"
            className={classes.backButton}
            variant="outlined"
            onClick={() => history.push(`/${exam}/${coordinatorExamRegistrations.route}`)}
          >
            {t('exam->>RegistrationSummary.BackToRegisteredExams')}
          </Button>
        </ContentTemplate>
      )}
    </Box>
  );
};

export default CoordinatorPaymentSummary;
